import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { List } from 'immutable';
import { compose, Dispatch } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
import { SportId } from 'app/lib/betting/constants';
import { getStakeTypeGroupTitleConf } from 'app/lib/matchGridStakes';
import Dropdown, { ChangeHandler } from 'app/widgets/Dropdown/Single';

import { StakeTypeGroup } from '../../constants';
import { getDataGaEventByNamespace } from '../../utils/data-ga-event-by-namespace';
import DropdownComponents, { ColumnOption } from './DropdownComponents';

import styles from './ColumnsHeader.module.scss';

// ---

type StoreProps = {
  stakeTypeGroupIds: List<number>;
  visibleStakeTypeGroup: List<keyof typeof StakeTypeGroup>;
  remainingGridStakeTypeGroupIds: List<keyof typeof StakeTypeGroup>;
};

type ReactReduxProps = {
  dispatch: Dispatch;
  $betting: BettingContextProps;
};

type GridColumnsHeaderProps = {
  sportId: SportId;
  className?: string;
};

const GridColumnsHeader: FC<GridColumnsHeaderProps & ReactReduxProps & StoreProps> = ({
  sportId,
  className,
  stakeTypeGroupIds,
  visibleStakeTypeGroup,
  remainingGridStakeTypeGroupIds,
  $betting,
  dispatch,
}) => {
  const [currentFilterId, setCurrentFilterId] = useState<null | number>(null);
  const { lang } = useTranslation();
  const handleChangeSelectedStakeTypes: ChangeHandler<ColumnOption> = useCallback((option) => {
    if (!option || !('value' in option)) {
      console.error('GridColumnsHeader -> handleChangeSelectedStakeTypes -> `option` not found', option);
      return;
    }

    const {
      grid: {
        actions: { changeStakeTypeGroup },
      },
    } = $betting;


    const selectedFilterIndex = stakeTypeGroupIds.findIndex(
      (id) => id === option.value,
    );

    const currentFilterIndex = stakeTypeGroupIds.findIndex(
      (id) => id === currentFilterId,
    );

    if (currentFilterId) {
      const stakeGroupTypeIds = stakeTypeGroupIds
        .set(currentFilterIndex, option.value)
        .set(selectedFilterIndex, currentFilterId);

      dispatch(changeStakeTypeGroup({
        sportId,
        stakeGroupTypeIds,
      }));
    }
  }, [currentFilterId, stakeTypeGroupIds]);

  const filterOptions = useMemo(() => {
    const options: ColumnOption[] = [];

    visibleStakeTypeGroup.concat(remainingGridStakeTypeGroupIds).forEach((id) => {
      options.push({
        label: getStakeTypeGroupTitleConf(id, lang),
        value: id,
      });
    });

    return options;
  }, [visibleStakeTypeGroup, remainingGridStakeTypeGroupIds, lang]);

  const setFilterId = useCallback((stakeTypeGroupId: number) => {
    return () => setCurrentFilterId(stakeTypeGroupId);
  }, []);

  const components = DropdownComponents(
    getColumnsHeaderPosition(visibleStakeTypeGroup, currentFilterId),
    getDataGaEventByNamespace($betting.namespace, 'changeLayout'),
  );
  return (
    <div className={classNames(styles.tableContainer, className)}>
      {visibleStakeTypeGroup.map((stakeTypeGroupId, index) => (
        <Dropdown<ColumnOption> dynamicOptions
                                closeMenuOnScroll
                                key={stakeTypeGroupId}
                                className={styles.select}
                                value={filterOptions[index]}
                                openMenuOnFocus
                                isSearchable={false}
                                maxMenuHeight={155}
                                menuPortalTarget={process.env.SERVER ? null : document.body}
                                menuPlacement="bottom"
                                options={filterOptions}
                                onMenuOpen={setFilterId(stakeTypeGroupId)}
                                onChange={handleChangeSelectedStakeTypes}
                                components={components} />
      ))}
    </div>
  );
};

// ---

const mapStateToProps = (_: ReduxState, {
  $betting: {
    grid: {
      selectors: {
        stakeTypeGroupBySportId,
        createVisibleStakeTypeGroupIdsSelector,
      },
    },
  },
}: BettingContextProps) => {
  const visibleStakeTypeGroupSelector = createVisibleStakeTypeGroupIdsSelector();

  return (state: ReduxState, { sportId }: GridColumnsHeaderProps): StoreProps => {
    const stakeTypeGroupIds = stakeTypeGroupBySportId(state, { sportId }); // no needs in `reselect`
    const visibleStakeTypeGroup = visibleStakeTypeGroupSelector(state, { sportId });

    return {
      stakeTypeGroupIds,
      visibleStakeTypeGroup,
      remainingGridStakeTypeGroupIds:
        stakeTypeGroupIds && stakeTypeGroupIds.slice(visibleStakeTypeGroup.size),
    };
  };
};

function getColumnsHeaderPosition(stakes: List<number>, filterId: number | null) {
  return stakes.findIndex((stakeTypeGroupId) => (
    stakeTypeGroupId === filterId
  )) + 1;
}

export default compose<FC<GridColumnsHeaderProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(GridColumnsHeader);
